import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { PortalNav } from "@/components/PortalNav";
import { EmailToggle } from "@/components/EmailToggle";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  UserPlus, Loader2, Copy, Check, Users, FileCheck2, Download, X, Eye, KeyRound, Link2, Pencil, FileText, Mail, Trash2,
} from "lucide-react";
import { toast } from "sonner";

export default function AdminDashboard() {
  const [customers, setCustomers] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [confirmResend, setConfirmResend] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [credential, setCredential] = useState(null);
  const [viewSub, setViewSub] = useState(null);
  const [editCustomer, setEditCustomer] = useState(null);
  const [search, setSearch] = useState("");

  const loadData = async () => {
    try {
      const [c, s] = await Promise.all([api.get("/admin/customers"), api.get("/admin/submissions")]);
      setCustomers(c.data.customers);
      setSubmissions(s.data.submissions);
    } catch (e) {
      /* dashboard load failed; UI shows empty state */
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const submissionFor = (customerId) => submissions.find((s) => s.customer_id === customerId);

  const resendLogin = async (c) => {    try {
      const { data } = await api.post(`/admin/customers/${c.id}/resend-login`);
      setCredential(data);
      toast.success(`Login email re-sent to ${data.email}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to resend");
    }
  };

  return (
    <div className="min-h-screen bg-[#F4F4F5] md:pl-[var(--sb-w,16rem)]">
      <PortalNav />
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
          <div className="flex items-end justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-2">Admin Console</p>
              <h1 className="font-display font-extrabold text-4xl tracking-tight text-[#0F172A]">Dashboard</h1>
            </div>
            <div className="flex items-center gap-3">
              <EmailToggle />
              <button
                onClick={() => setShowCreate(true)}
                data-testid="admin-add-customer"
                className="flex items-center gap-2 bg-[#0F172A] text-white px-5 py-3 font-medium hover:bg-[#1E293B] transition-colors"
              >
                <UserPlus className="h-4 w-4" /> New Prospect
              </button>
            </div>
          </div>

          {/* Stats */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
            <Stat icon={Users} label="Customers" value={customers.length} />
            <Stat icon={FileCheck2} label="Forms submitted" value={submissions.length} />
            <Stat icon={KeyRound} label="Pending" value={customers.length - submissions.length} />
          </div>

          {/* Customers table */}
          <div className="mt-8">
            <div className="bg-white border border-zinc-200">
                <div className="p-5 border-b border-zinc-200 flex items-center justify-between gap-4 flex-wrap">
                  <h2 className="font-display font-bold text-[#0F172A]">Customers</h2>
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    data-testid="customer-search"
                    placeholder="Search company, email, or account #"
                    className="w-full sm:w-80 px-3 py-2 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent"
                  />
                </div>
                {customers.length === 0 ? (
                  <div className="p-12 text-center text-zinc-400 text-sm" data-testid="no-customers">
                    No customers yet. Click "New Customer" to create your first one.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="customers-table">
                      <thead>
                        <tr className="text-left text-[11px] uppercase tracking-wide text-zinc-400 border-b border-zinc-200">
                          <th className="px-5 py-3 font-medium">Company</th>
                          <th className="px-5 py-3 font-medium">Contact</th>
                          <th className="px-5 py-3 font-medium">Requested / Approved</th>
                          <th className="px-5 py-3 font-medium">Approval</th>
                          <th className="px-5 py-3 font-medium">Status</th>
                          <th className="px-5 py-3 font-medium">References</th>
                          <th className="px-3 py-3 font-medium text-center">EDIT</th>
                          <th className="px-3 py-3 font-medium text-center">LOGIN</th>
                          <th className="px-3 py-3 font-medium text-center">DELETE</th>
                          <th className="px-3 py-3 font-medium text-center">CREDIT APP</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-zinc-100">
                        {customers.filter((c) => {
                          const t = search.trim().toLowerCase();
                          if (!t) return true;
                          return [c.company_name, c.email, c.account_number, c.contact_name].some((v) => (v || "").toLowerCase().includes(t));
                        }).map((c) => {
                          const sub = submissionFor(c.id);
                          const emailedRefs = (sub?.references || []).filter((r) => r.token);
                          const responded = emailedRefs.filter((r) => r.credit_status === "completed").length;
                          const total = emailedRefs.length;
                          const allIn = total > 0 && responded === total;
                          return (
                            <tr key={c.id} className={`transition-colors ${c.is_prospect ? "bg-[#2563EB]/10 hover:bg-[#2563EB]/15" : "hover:bg-zinc-50"}`} data-testid={`customer-row-${c.id}`}>
                              <td className={`px-5 py-4 ${c.is_prospect ? "border-l-4 border-[#2563EB]" : ""}`}>
                                <div className="font-medium flex items-center gap-2 text-[#0F172A]">
                                  {c.company_name}
                                  {c.is_prospect && <span data-testid={`admin-prospect-${c.id}`} className="text-[10px] uppercase tracking-wide text-white bg-[#2563EB] px-1.5 py-0.5">Prospect</span>}
                                </div>
                                <div className="text-xs text-zinc-400 font-mono">{c.email}</div>
                              </td>
                              <td className="px-5 py-4 text-zinc-600">
                                <div>{c.contact_name}</div>
                                <div className="text-xs text-zinc-400 font-mono">{c.phone}</div>
                              </td>
                              <td className="px-5 py-4 font-mono text-sm whitespace-nowrap" data-testid={`requested-amount-${c.id}`}>
                                <span className="text-zinc-500">{c.requested_amount != null ? `$${Number(c.requested_amount).toLocaleString()}` : "—"}</span>
                                <span className="text-zinc-300 mx-1.5">/</span>
                                <span className="text-[#0F172A]" data-testid={`approved-amount-${c.id}`}>{c.approved_amount != null ? `$${Number(c.approved_amount).toLocaleString()}` : "—"}</span>
                              </td>
                              <td className="px-5 py-4">
                                <ApprovalBadge status={c.approval_status} testid={`approval-${c.id}`} />
                              </td>
                              <td className="px-5 py-4">
                                {sub ? (
                                  <span className="inline-flex items-center gap-1.5 text-[#10B981] text-xs font-medium">
                                    <span className="h-1.5 w-1.5 bg-[#10B981] rounded-full" /> Submitted
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1.5 text-zinc-400 text-xs">
                                    <span className="h-1.5 w-1.5 bg-zinc-300 rounded-full" /> Pending
                                  </span>
                                )}
                              </td>
                              <td className="px-5 py-4">
                                {total === 0 ? (
                                  <span className="text-xs text-zinc-300" data-testid={`ref-progress-${c.id}`}>—</span>
                                ) : (
                                  <span
                                    data-testid={`ref-progress-${c.id}`}
                                    className={`inline-flex items-center gap-2 text-xs font-medium ${allIn ? "text-[#10B981]" : "text-[#0F172A]"}`}
                                  >
                                    <span className="font-mono">{responded}/{total}</span>
                                    <span className="text-zinc-400 font-normal">responded</span>
                                  </span>
                                )}
                              </td>
                              <td className="px-3 py-4 text-center">
                                <button
                                  onClick={() => setEditCustomer(c)}
                                  data-testid={`edit-customer-${c.id}`}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-zinc-300 text-zinc-600 hover:border-[#0F172A] hover:text-[#0F172A] transition-colors"
                                >
                                  <Pencil className="h-3.5 w-3.5" /> Edit
                                </button>
                              </td>
                              <td className="px-3 py-4 text-center">
                                <button
                                  onClick={() => setConfirmResend(c)}
                                  data-testid={`resend-login-${c.id}`}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-zinc-300 text-zinc-600 hover:border-[#0F172A] hover:text-[#0F172A] transition-colors"
                                  title="Reset the password and email fresh login details to the customer"
                                >
                                  <Mail className="h-3.5 w-3.5" /> Resend
                                </button>
                              </td>
                              <td className="px-3 py-4 text-center">
                                <button
                                  onClick={() => setConfirmDelete(c)}
                                  data-testid={`delete-customer-${c.id}`}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-zinc-300 text-zinc-500 hover:border-[#EF4444] hover:text-[#EF4444] transition-colors"
                                  title="Permanently delete this customer and their references/deals"
                                >
                                  <Trash2 className="h-3.5 w-3.5" /> Delete
                                </button>
                              </td>
                              <td className="px-3 py-4 text-center">
                                <button
                                  onClick={() => setViewSub({ customer: c, sub })}
                                  data-testid={`view-sub-${c.id}`}
                                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs border border-[#0F172A] text-[#0F172A] hover:bg-zinc-100 transition-colors"
                                >
                                  <Eye className="h-3.5 w-3.5" /> View
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
        </motion.div>
      </main>

      {showCreate && (
        <AdminCreateCustomerModal
          onClose={() => setShowCreate(false)}
          onCreated={(cred) => {
            setShowCreate(false);
            setCredential(cred);
            loadData();
          }}
        />
      )}
      {credential && <CredentialModal cred={credential} onClose={() => setCredential(null)} />}
      {confirmResend && (
        <ConfirmResendModal
          customer={confirmResend}
          onCancel={() => setConfirmResend(null)}
          onConfirm={async () => { const c = confirmResend; setConfirmResend(null); await resendLogin(c); }}
        />
      )}
      {confirmDelete && (
        <ConfirmDeleteModal
          customer={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onDeleted={() => { setConfirmDelete(null); loadData(); }}
        />
      )}      {viewSub && <SubmissionModal customer={viewSub.customer} sub={viewSub.sub} onClose={() => setViewSub(null)} />}
      {editCustomer && (
        <EditCustomerModal
          customer={editCustomer}
          onClose={() => setEditCustomer(null)}
          onSaved={() => {
            setEditCustomer(null);
            loadData();
          }}
        />
      )}
    </div>
  );
}

function ApprovalBadge({ status, testid }) {
  const s = status || "Pending";
  const map = {
    Approved: "text-[#10B981]",
    Declined: "text-[#EF4444]",
    Pending: "text-zinc-400",
  };
  const dot = {
    Approved: "bg-[#10B981]",
    Declined: "bg-[#EF4444]",
    Pending: "bg-zinc-300",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${map[s] || map.Pending}`} data-testid={testid}>
      <span className={`h-1.5 w-1.5 rounded-full ${dot[s] || dot.Pending}`} /> {s}
    </span>
  );
}

function Stat({ icon: Icon, label, value }) {  return (
    <div className="bg-white p-6">
      <Icon className="h-5 w-5 text-[#D4AF37]" />
      <div className="mt-3 font-display font-extrabold text-3xl text-[#0F172A]">{value}</div>
      <div className="text-xs uppercase tracking-wide text-zinc-400 mt-1">{label}</div>
    </div>
  );
}

function FormField({ label, value, onChange, type = "text", testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#0F172A] mb-1.5">{label}</label>
      <input
        type={type}
        required
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow"
      />
    </div>
  );
}

function AdminCreateCustomerModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ company_name: "", contact_name: "", email: "", phone: "", account_number: "" });
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setCreating(true);
    try {
      const { data } = await api.post("/admin/customers", form);
      toast.success("Customer account created");
      onCreated(data.credentials);
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F172A]/60 backdrop-blur-sm" data-testid="admin-create-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="bg-white border border-zinc-200 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 flex items-center justify-between p-5 border-b border-zinc-200 bg-[#0F172A] text-white">
          <div className="flex items-center gap-2">
            <UserPlus className="h-4 w-4 text-[#D4AF37]" />
            <h3 className="font-display font-bold">New Prospect</h3>
          </div>
          <button onClick={onClose} data-testid="admin-close-create"><X className="h-5 w-5 text-zinc-300 hover:text-white" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4" data-testid="create-customer-form">
          <FormField label="Company Name" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} testid="cc-company" />
          <FormField label="Contact Name" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testid="cc-contact" />
          <FormField label="Email Address" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="cc-email" />
          <FormField label="Phone Number" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="cc-phone" />
          <FormField label="Account Number" value={form.account_number} onChange={(v) => setForm({ ...form, account_number: v })} testid="cc-account" />
          {error && <p className="text-sm text-[#EF4444]" data-testid="cc-error">{error}</p>}
          <button
            type="submit"
            disabled={creating}
            data-testid="cc-submit"
            className="w-full flex items-center justify-center gap-2 bg-[#0F172A] text-white py-3 font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create & generate login"}
          </button>
          <p className="text-xs text-zinc-400 leading-relaxed">
            A username &amp; password will be generated. Copy and share them with the customer so they can access the portal.
          </p>
        </form>
      </motion.div>
    </div>
  );
}

function ConfirmDeleteModal({ customer, onCancel, onDeleted }) {
  const [busy, setBusy] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const del = async () => {
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/customers/${customer.id}`);
      const d = data.deleted || {};
      toast.success(`Deleted ${customer.company_name} (${d.submissions || 0} submission(s), ${d.deals || 0} deal(s))`);
      onDeleted();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-[#0F172A]/60 backdrop-blur-sm" data-testid="confirm-delete-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="bg-white border border-zinc-200 w-full max-w-md">
        <div className="flex items-center gap-2 p-5 border-b border-zinc-200 bg-[#EF4444] text-white">
          <Trash2 className="h-4 w-4" />
          <h3 className="font-display font-bold">Delete customer?</h3>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-zinc-600 leading-relaxed">
            This permanently removes <strong className="text-[#0F172A]">{customer.company_name}</strong> ({customer.email}) along with their credit submission, all trade references, and any CRM deals/calls. This cannot be undone.
          </p>
          <div>
            <label className="block text-xs uppercase tracking-wide text-zinc-400 mb-1.5">Type DELETE to confirm</label>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              data-testid="confirm-delete-input"
              placeholder="DELETE"
              className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#EF4444]"
            />
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={del}
              disabled={busy || confirmText.trim() !== "DELETE"}
              data-testid="confirm-delete-yes"
              className="flex-1 flex items-center justify-center gap-2 bg-[#EF4444] text-white py-2.5 text-sm font-medium hover:bg-[#dc2626] transition-colors disabled:opacity-50"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="h-4 w-4" /> Delete permanently</>}
            </button>
            <button onClick={onCancel} disabled={busy} data-testid="confirm-delete-cancel" className="px-4 py-2.5 text-sm border border-zinc-300 text-zinc-600 hover:text-[#0F172A] transition-colors">Cancel</button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function ConfirmResendModal({ customer, onCancel, onConfirm }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-[#0F172A]/60 backdrop-blur-sm" data-testid="confirm-resend-modal">
      <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="bg-white border border-zinc-200 w-full max-w-md">
        <div className="flex items-center gap-2 p-5 border-b border-zinc-200 bg-[#0F172A] text-white">
          <Mail className="h-4 w-4 text-[#D4AF37]" />
          <h3 className="font-display font-bold">Resend login?</h3>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-zinc-600 leading-relaxed">
            This will <strong className="text-[#0F172A]">generate a new password</strong> for <strong className="text-[#0F172A]">{customer.company_name}</strong> ({customer.email}) and email them fresh login details. Their current password will stop working.
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={async () => { setBusy(true); await onConfirm(); }}
              disabled={busy}
              data-testid="confirm-resend-yes"
              className="flex-1 flex items-center justify-center gap-2 bg-[#0F172A] text-white py-2.5 text-sm font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4" /> Reset & send</>}
            </button>
            <button onClick={onCancel} disabled={busy} data-testid="confirm-resend-cancel" className="px-4 py-2.5 text-sm border border-zinc-300 text-zinc-600 hover:text-[#0F172A] transition-colors">Cancel</button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function CredentialModal({ cred, onClose }) {  const [copied, setCopied] = useState(false);
  const text = `Peregrine Credit Reference Portal\nEmail: ${cred.email}\nPassword: ${cred.password}`;
  const copy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("Credentials copied");
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F172A]/60 backdrop-blur-sm" data-testid="credential-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white border border-zinc-200 w-full max-w-md"
      >
        <div className="flex items-center justify-between p-5 border-b border-zinc-200 bg-[#0F172A] text-white">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-[#D4AF37]" />
            <h3 className="font-display font-bold">Login credentials generated</h3>
          </div>
          <button onClick={onClose} data-testid="close-credential"><X className="h-5 w-5 text-zinc-300 hover:text-white" /></button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-zinc-500">
            Share these with the customer. This password is shown once — copy it now.
          </p>
          <div className="border border-zinc-200 divide-y divide-zinc-100 font-mono text-sm">
            <CredRow label="Email" value={cred.email} />
            <CredRow label="Password" value={cred.password} highlight />
          </div>
          <button
            onClick={copy}
            data-testid="copy-credentials"
            className="w-full flex items-center justify-center gap-2 bg-[#0F172A] text-white py-3 font-medium hover:bg-[#1E293B] transition-colors"
          >
            {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy all</>}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

function CredRow({ label, value, highlight }) {
  return (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-xs uppercase tracking-wide text-zinc-400">{label}</span>
      <span className={highlight ? "text-[#0F172A] font-medium" : "text-zinc-700"}>{value}</span>
    </div>
  );
}

function AdminCopyLine({ label, value, testid }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    toast.success(`${label} copied`);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <div className="flex items-center gap-2">
      <span className="text-[11px] uppercase tracking-wide text-zinc-400 w-20 shrink-0">{label}</span>
      <code className="flex-1 font-mono text-xs bg-white border border-zinc-200 px-2 py-1.5 truncate">{value}</code>
      <button onClick={copy} data-testid={testid} className="p-1.5 text-zinc-400 hover:text-[#0F172A] transition-colors" title={`Copy ${label}`}>
        {copied ? <Check className="h-4 w-4 text-[#10B981]" /> : <Copy className="h-4 w-4" />}
      </button>
    </div>
  );
}

function CD({ label, value }) {
  return (
    <div>
      <span className="text-zinc-400 block">{label}</span>
      <span className="text-[#0F172A]">{value || "—"}</span>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between px-4 py-2.5">
      <span className="text-zinc-400 text-xs uppercase tracking-wide">{label}</span>
      <span className="text-[#0F172A]">{value || "—"}</span>
    </div>
  );
}

function SubmissionModal({ customer, sub, onClose }) {
  const resendRef = async (token) => {
    try {
      const { data } = await api.post("/admin/references/resend", { token });
      toast.success(`Reference request re-sent to ${data.email}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to resend");
    }
  };
  const download = () => {
    const s = sub || {};
    const lines = [
      "PEREGRINE AIR SYSTEMS — CREDIT / TRADE REFERENCE SUBMISSION",
      "",
      `Company: ${customer.company_name || ""}`,
      `Contact: ${customer.contact_name || ""}`,
      `Email: ${customer.email || ""}`,
      `Phone: ${customer.phone || ""}`,
      `Account #: ${customer.account_number || ""}`,
      `Approval status: ${customer.approval_status || "Pending"}`,
      `Requested amount: ${customer.requested_amount != null ? `$${Number(customer.requested_amount).toLocaleString()}` : ""}`,
      `Approved amount: ${customer.approved_amount != null ? `$${Number(customer.approved_amount).toLocaleString()}` : ""}`,
      `Submitted: ${s.updated_at || s.created_at || "Not yet submitted"}`,
      "",
      "CUSTOMER INFORMATION",
      `  Mailing address: ${s.customer_info?.mailing_address || ""}`,
      `  A/P contact name: ${s.customer_info?.ap_contact_name || ""}`,
      `  A/P email: ${s.customer_info?.ap_email || ""}`,
      `  Requires PO#: ${s.customer_info?.requires_po || ""}`,
      `  How PO# acquired: ${s.customer_info?.po_acquisition || ""}`,
      `  Tax exempt: ${s.customer_info?.tax_exempt || ""}`,
      "",
      "TRADE REFERENCES",
      ...(s.references || []).flatMap((r, i) => {
        const base = `  ${i + 1}. ${r.company_name} | ${r.contact} | ${r.email} [${r.credit_status || "no email"}]`;
        const c = r.credit_data;
        if (!c) return [base];
        return [
          base,
          `       Reference company: ${c.reference_company || ""}`,
          `       Account length: ${c.account_length || ""}`,
          `       Terms: ${c.terms || ""}`,
          `       Current balance: ${c.current_balance || ""}`,
          `       Amount past due: ${c.amount_past_due || ""}`,
          `       Recent high credit: ${c.high_credit || ""}`,
          `       Payment history (30/45/60/90): ${c.payment_30 || ""} / ${c.payment_40 || ""} / ${c.payment_60 || ""} / ${c.payment_90 || ""}`,
        ];
      }),
      "",
      `Signature: ${s.signature || ""}`,
      `Agreed to Net 30 terms: ${s.agreed_terms ? "Yes" : "No"}`,
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `PAS-references-${(customer.company_name || "customer").replace(/\s+/g, "-")}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F172A]/60 backdrop-blur-sm" data-testid="submission-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white border border-zinc-200 w-full max-w-2xl max-h-[85vh] overflow-y-auto"
      >
        <div className="sticky top-0 flex items-center justify-between p-5 border-b border-zinc-200 bg-white">
          <div>
            <h3 className="font-display font-bold text-[#0F172A]">{customer.company_name}</h3>
            <p className="text-xs text-zinc-400 font-mono">{customer.email}</p>
            {customer.account_number && <p className="text-xs text-[#B8860B] font-mono" data-testid="sub-account-number">Account #: {customer.account_number}</p>}
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/submissions/${customer.id}/pdf`}
              target="_blank"
              rel="noreferrer"
              data-testid="view-sub-pdf"
              className="flex items-center gap-2 px-3 py-2 text-sm border border-[#0F172A] text-[#0F172A] hover:bg-zinc-100 transition-colors"
            >
              <FileText className="h-4 w-4" /> PDF
            </a>
            <button
              onClick={download}
              data-testid="download-sub"
              className="flex items-center gap-2 px-3 py-2 text-sm bg-[#0F172A] text-white hover:bg-[#1E293B] transition-colors"
            >
              <Download className="h-4 w-4" /> Text
            </button>
            <button onClick={onClose} data-testid="close-submission"><X className="h-5 w-5 text-zinc-400 hover:text-[#0F172A]" /></button>
          </div>
        </div>
        <div className="p-6 space-y-6">
          <div data-testid="admin-credit-application">
            <h4 className="text-xs uppercase tracking-wide text-zinc-400 mb-3">Credit Application</h4>
            <div className="border border-zinc-200 divide-y divide-zinc-100 text-sm">
              <InfoRow label="Company" value={customer.company_name} />
              <InfoRow label="Contact" value={customer.contact_name} />
              <InfoRow label="Phone" value={customer.phone} />
              <InfoRow label="Account #" value={customer.account_number} />
              <InfoRow label="Approval status" value={customer.approval_status || "Pending"} />
              <InfoRow label="Requested amount" value={customer.requested_amount != null ? `$${Number(customer.requested_amount).toLocaleString()}` : "—"} />
              <InfoRow label="Approved amount" value={customer.approved_amount != null ? `$${Number(customer.approved_amount).toLocaleString()}` : "—"} />
              <InfoRow label="Note to customer" value={customer.approval_note} />
              <InfoRow label="Application status" value={sub ? (sub.signature ? "Submitted" : "Draft (unsigned)") : "Not yet submitted"} />
            </div>
            {customer.tax_exempt_file && (
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/customers/${customer.id}/tax-exempt`}
                target="_blank"
                rel="noreferrer"
                data-testid="view-sub-tax-exempt"
                className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 mt-3 border border-[#0F172A] text-[#0F172A] hover:bg-zinc-100 transition-colors"
              >
                <FileText className="h-3.5 w-3.5" /> View Tax Exempt Certificate
              </a>
            )}
          </div>
          {sub?.customer_info && Object.values(sub.customer_info).some(Boolean) && (
            <div data-testid="admin-customer-info">
              <h4 className="text-xs uppercase tracking-wide text-zinc-400 mb-3">Customer Information</h4>
              <div className="border border-zinc-200 divide-y divide-zinc-100 text-sm">
                <InfoRow label="Mailing address" value={sub.customer_info.mailing_address} />
                <InfoRow label="A/P contact name" value={sub.customer_info.ap_contact_name} />
                <InfoRow label="A/P email" value={sub.customer_info.ap_email} />
                <InfoRow label="Requires PO#" value={sub.customer_info.requires_po} />
                <InfoRow label="How PO# acquired" value={sub.customer_info.po_acquisition} />
                <InfoRow label="Tax exempt" value={sub.customer_info.tax_exempt} />
              </div>
            </div>
          )}
          <div>
            <h4 className="text-xs uppercase tracking-wide text-zinc-400 mb-3">Trade References</h4>
            <div className="border border-zinc-200 divide-y divide-zinc-100">
              {(sub?.references || []).length === 0 && (
                <div className="p-4 text-sm text-zinc-400" data-testid="admin-no-refs">No credit application submitted yet.</div>
              )}
              {(sub?.references || []).map((r, i) => {
                const link = r.token ? `${window.location.origin}/reference/${r.token}` : null;
                const cd = r.credit_data;
                return (
                  <div key={`${r.token || r.email || "ref"}-${i}`} className="p-4 space-y-3" data-testid={`admin-ref-${i}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm">
                      <div><span className="text-zinc-400 text-xs block">Company</span>{r.company_name || "—"}</div>
                      <div><span className="text-zinc-400 text-xs block">Contact</span>{r.contact || "—"}</div>
                      <div><span className="text-zinc-400 text-xs block">Email</span><span className="font-mono text-xs break-all">{r.email || "—"}</span></div>
                    </div>
                    {link && (
                      <div className="border border-zinc-200 bg-zinc-50 p-3 space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-zinc-500">
                            <Link2 className="h-3.5 w-3.5 text-[#D4AF37]" /> Credit-check link
                          </span>
                          {r.credit_status === "completed" ? (
                            <span className="text-[#10B981] text-xs font-medium">Completed</span>
                          ) : (
                            <span className="text-zinc-400 text-xs">Awaiting response</span>
                          )}
                        </div>
                        <AdminCopyLine label="Link" value={link} testid={`admin-copy-link-${i}`} />
                        <AdminCopyLine label="Password" value={r.password} testid={`admin-copy-pass-${i}`} />
                        <button
                          onClick={() => resendRef(r.token)}
                          data-testid={`admin-resend-ref-${i}`}
                          className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-[#0F172A] text-[#0F172A] hover:bg-zinc-100 transition-colors"
                        >
                          <Mail className="h-3.5 w-3.5" /> Resend request email
                        </button>
                      </div>
                    )}
                    {r.uploaded_file && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/reference/${r.token}/file`}
                        target="_blank"
                        rel="noreferrer"
                        data-testid={`admin-view-pdf-${i}`}
                        className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-[#0F172A] text-[#0F172A] hover:bg-zinc-100 transition-colors"
                      >
                        <FileText className="h-3.5 w-3.5" /> View uploaded PDF ({r.uploaded_file.filename})
                      </a>
                    )}
                    {cd && (
                      <div className="border border-[#10B981]/30 bg-[#10B981]/5 p-3 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs" data-testid={`admin-ref-credit-${i}`}>
                        <CD label="Ref. company" value={cd.reference_company} />
                        <CD label="Account length" value={cd.account_length} />
                        <CD label="Terms" value={cd.terms} />
                        <CD label="Current balance" value={cd.current_balance} />
                        <CD label="Amount past due" value={cd.amount_past_due} />
                        <CD label="Recent high credit" value={cd.high_credit} />
                        <CD label="30 days" value={cd.payment_30} />
                        <CD label="45 days" value={cd.payment_40} />
                        <CD label="60 days" value={cd.payment_60} />
                        <CD label="90 days" value={cd.payment_90} />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-zinc-400 text-xs block">Signature</span>{sub?.signature || "—"}</div>
            <div><span className="text-zinc-400 text-xs block">Net 30 terms</span>{sub?.agreed_terms ? "Agreed" : "Not agreed"}</div>
          </div>
        </div>
      </motion.div>
    </div>
  );
}


function EditCustomerModal({ customer, onClose, onSaved }) {
  const [form, setForm] = useState({
    company_name: customer.company_name || "",
    contact_name: customer.contact_name || "",
    email: customer.email || "",
    phone: customer.phone || "",
    account_number: customer.account_number || "",
    requested_amount: customer.requested_amount != null ? String(customer.requested_amount) : "",
    approved_amount: customer.approved_amount != null ? String(customer.approved_amount) : "",
    approval_status: customer.approval_status || (customer.approved ? "Approved" : "Pending"),
    approval_note: customer.approval_note || "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  const [issuedPassword, setIssuedPassword] = useState(null);
  const [copied, setCopied] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      await api.patch(`/admin/customers/${customer.id}`, {
        ...form,
        requested_amount: form.requested_amount === "" ? null : parseFloat(form.requested_amount),
        approved_amount: form.approved_amount === "" ? null : parseFloat(form.approved_amount),
        approval_status: form.approval_status,
        approval_note: form.approval_note,
      });
      toast.success("Customer updated");
      onSaved();
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const resetPassword = async () => {
    setError("");
    setResetting(true);
    try {
      const { data } = await api.post(`/admin/customers/${customer.id}/reset-password`, {
        new_password: newPassword.trim() || null,
      });
      setIssuedPassword(data.password);
      setNewPassword("");
      toast.success("Password updated");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setResetting(false);
    }
  };

  const copyPw = () => {
    navigator.clipboard.writeText(`Email: ${form.email}\nPassword: ${issuedPassword}`);
    setCopied(true);
    toast.success("Credentials copied");
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-[#0F172A]/60 backdrop-blur-sm" data-testid="edit-customer-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white border border-zinc-200 w-full max-w-md max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 flex items-center justify-between p-5 border-b border-zinc-200 bg-[#0F172A] text-white">
          <div className="flex items-center gap-2">
            <Pencil className="h-4 w-4 text-[#D4AF37]" />
            <h3 className="font-display font-bold">Edit customer</h3>
          </div>
          <button onClick={onClose} data-testid="close-edit"><X className="h-5 w-5 text-zinc-300 hover:text-white" /></button>
        </div>

        <form onSubmit={save} className="p-6 space-y-4" data-testid="edit-customer-form">
          <FormField label="Company Name" value={form.company_name} onChange={(v) => setForm({ ...form, company_name: v })} testid="edit-company" />
          <FormField label="Contact Name" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testid="edit-contact" />
          <FormField label="Email Address" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="edit-email" />
          <FormField label="Phone Number" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="edit-phone" />
          <FormField label="Account Number" value={form.account_number} onChange={(v) => setForm({ ...form, account_number: v })} testid="edit-account" />
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Requested $</label>
              <input
                type="number" min="0" step="0.01"
                value={form.requested_amount}
                onChange={(e) => setForm({ ...form, requested_amount: e.target.value })}
                data-testid="edit-requested-amount"
                placeholder="0.00"
                className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Approved $</label>
              <input
                type="number" min="0" step="0.01"
                value={form.approved_amount}
                onChange={(e) => setForm({ ...form, approved_amount: e.target.value })}
                data-testid="edit-approved-amount"
                placeholder="0.00"
                className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Approval Status</label>
            <select
              value={form.approval_status}
              onChange={(e) => setForm({ ...form, approval_status: e.target.value })}
              data-testid="edit-approval-status"
              className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37]"
            >
              <option value="Pending">Pending</option>
              <option value="Approved">Approved</option>
              <option value="Declined">Declined</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-1.5">Note to customer <span className="text-zinc-400 font-normal">(optional — included in the approval/decline email)</span></label>
            <textarea
              value={form.approval_note}
              onChange={(e) => setForm({ ...form, approval_note: e.target.value })}
              data-testid="edit-approval-note"
              rows={3}
              placeholder="e.g. Welcome aboard! Your account is ready with Net 30 terms."
              className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow resize-y"
            />
          </div>
          {customer.tax_exempt_file && (
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/customers/${customer.id}/tax-exempt`}
              target="_blank"
              rel="noreferrer"
              data-testid="admin-view-tax-exempt"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 border border-[#0F172A] text-[#0F172A] hover:bg-zinc-100 transition-colors"
            >
              <FileText className="h-3.5 w-3.5" /> View Tax Exempt Certificate
            </a>
          )}
          {error && <p className="text-sm text-[#EF4444]" data-testid="edit-error">{error}</p>}
          <button
            type="submit"
            disabled={saving}
            data-testid="edit-save"
            className="w-full flex items-center justify-center gap-2 bg-[#0F172A] text-white py-3 font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}
          </button>
        </form>

        <div className="p-6 border-t border-zinc-200 space-y-4">
          <div className="flex items-center gap-2">
            <KeyRound className="h-4 w-4 text-[#D4AF37]" />
            <h4 className="font-display font-bold text-[#0F172A] text-sm">Change password</h4>
          </div>
          <p className="text-xs text-zinc-500">Leave blank to auto-generate a secure password, or type a new one (min 8 characters).</p>
          <input
            type="text"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            data-testid="edit-new-password"
            placeholder="New password (optional)"
            className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm font-mono outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow"
          />
          <button
            type="button"
            onClick={resetPassword}
            disabled={resetting}
            data-testid="edit-reset-password"
            className="w-full flex items-center justify-center gap-2 border border-[#0F172A] text-[#0F172A] py-3 font-medium hover:bg-zinc-100 transition-colors disabled:opacity-60"
          >
            {resetting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update password"}
          </button>

          {issuedPassword && (
            <div className="border border-[#10B981]/40 bg-[#10B981]/5 p-4 space-y-3" data-testid="issued-password">
              <p className="text-xs text-zinc-600">New password (share it with the customer — shown once):</p>
              <div className="flex items-center justify-between font-mono text-sm">
                <span className="text-zinc-400 text-xs uppercase">Password</span>
                <span className="text-[#0F172A] font-medium">{issuedPassword}</span>
              </div>
              <button
                type="button"
                onClick={copyPw}
                data-testid="copy-new-password"
                className="w-full flex items-center justify-center gap-2 bg-[#0F172A] text-white py-2.5 text-sm font-medium hover:bg-[#1E293B] transition-colors"
              >
                {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy email &amp; password</>}
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}
